import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listOrganizations,
  listSkillAccess,
  updateSkillAccess,
  type AdminOrg,
  type AdminSkill,
  type SkillVisibility,
} from '../api/admin';

type Filter = 'all' | SkillVisibility;

export function SkillsPage() {
  const skillsQuery = useQuery({ queryKey: ['admin', 'skills'], queryFn: listSkillAccess });
  const orgsQuery = useQuery({ queryKey: ['admin', 'organizations'], queryFn: listOrganizations });
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<Filter>('all');

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (skillsQuery.data ?? []).filter(
      (s) =>
        (filter === 'all' || s.visibility === filter) &&
        (!term ||
          s.title.toLowerCase().includes(term) ||
          s.name.toLowerCase().includes(term) ||
          s.organizations.some((o) => o.name.toLowerCase().includes(term))),
    );
  }, [skillsQuery.data, search, filter]);

  const privateCount = (skillsQuery.data ?? []).filter((s) => s.visibility === 'private').length;

  return (
    <div className="mx-auto max-w-5xl p-8">
      <div className="border-b border-slate-200 pb-6">
        <h1 className="text-3xl font-bold text-slate-900">Skills</h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Choose who sees each platform skill. A public skill is in every organization&apos;s catalogue. A private
          skill is listed and runs only for the organizations you add to it.
        </p>
      </div>

      <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search skills or organizations"
          aria-label="Search skills or organizations"
          className="min-h-11 w-full rounded-lg border border-slate-300 px-3 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200 sm:max-w-xs"
        />
        <div role="group" aria-label="Filter by visibility" className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
          {(['all', 'public', 'private'] as const).map((value) => (
            <button
              key={value}
              type="button"
              aria-pressed={filter === value}
              onClick={() => setFilter(value)}
              className={`min-h-9 cursor-pointer rounded-md px-3 text-sm font-medium capitalize transition-colors ${
                filter === value ? 'bg-purple-600 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {value === 'private' ? `Private (${privateCount})` : value}
            </button>
          ))}
        </div>
      </div>

      {skillsQuery.isLoading && <div className="py-16 text-center text-slate-500">Loading skills…</div>}
      {skillsQuery.isError && (
        <div role="alert" className="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Skills could not be loaded.
        </div>
      )}

      <ul className="mt-6 space-y-3">
        {shown.map((skill) => (
          <SkillRow key={skill.name} skill={skill} orgs={orgsQuery.data ?? []} />
        ))}
      </ul>
      {skillsQuery.data && shown.length === 0 && (
        <div className="py-12 text-center text-sm text-slate-500">No skills match.</div>
      )}
    </div>
  );
}

function SkillRow({ skill, orgs }: { skill: AdminSkill; orgs: AdminOrg[] }) {
  const queryClient = useQueryClient();
  const [visibility, setVisibility] = useState<SkillVisibility>(skill.visibility);
  const [orgIds, setOrgIds] = useState<string[]>(skill.organizations.map((o) => o.id));
  const [picking, setPicking] = useState('');

  const savedIds = skill.organizations.map((o) => o.id).sort().join(',');
  const dirty = visibility !== skill.visibility || [...orgIds].sort().join(',') !== savedIds;

  const mutation = useMutation({
    mutationFn: () => updateSkillAccess(skill.name, { visibility, organization_ids: orgIds }),
    onSuccess: (updated) => {
      queryClient.setQueryData<AdminSkill[]>(['admin', 'skills'], (rows) =>
        (rows ?? []).map((r) => (r.name === updated.name ? updated : r)),
      );
      setOrgIds(updated.organizations.map((o) => o.id));
    },
  });

  const nameOf = (id: string) =>
    orgs.find((o) => o.id === id)?.name ?? skill.organizations.find((o) => o.id === id)?.name ?? id;
  const addable = orgs.filter((o) => !orgIds.includes(o.id)).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <li className="rounded-xl border border-slate-200 bg-white p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-slate-900">{skill.title}</h2>
            <span className="rounded bg-slate-100 px-2 py-0.5 font-mono text-xs text-slate-600">{skill.name}</span>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-600">{skill.category}</span>
          </div>
          <p className="mt-1 text-sm text-slate-600">{skill.description}</p>
          {skill.hosts.length > 0 && (
            <p className="mt-1 text-xs text-slate-500">Hosts: {skill.hosts.join(', ')}</p>
          )}
          <p className="mt-1 text-xs text-slate-500">File default: {skill.default_visibility}</p>
        </div>

        <div role="radiogroup" aria-label={`Visibility of ${skill.title}`} className="inline-flex shrink-0 rounded-lg border border-slate-200 p-1">
          {(['public', 'private'] as const).map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={visibility === value}
              onClick={() => setVisibility(value)}
              className={`min-h-9 cursor-pointer rounded-md px-3 text-sm font-medium capitalize transition-colors ${
                visibility === value
                  ? value === 'private'
                    ? 'bg-amber-500 text-white'
                    : 'bg-emerald-600 text-white'
                  : 'text-slate-600 hover:bg-slate-50'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>

      {visibility === 'private' && (
        <div className="mt-4 border-t border-slate-100 pt-4">
          <div className="text-sm font-medium text-slate-700">Organizations that can see it</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {orgIds.map((id) => (
              <span key={id} className="inline-flex items-center gap-1 rounded-full bg-purple-50 py-1 pl-3 pr-1 text-sm text-purple-800">
                {nameOf(id)}
                <button
                  type="button"
                  onClick={() => setOrgIds(orgIds.filter((o) => o !== id))}
                  aria-label={`Remove ${nameOf(id)}`}
                  className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-full hover:bg-purple-100"
                >
                  ×
                </button>
              </span>
            ))}
            {orgIds.length === 0 && (
              <span className="text-sm text-amber-700">No organization yet — nobody will see this skill.</span>
            )}
          </div>
          <div className="mt-3 flex gap-2">
            <select
              value={picking}
              onChange={(e) => setPicking(e.target.value)}
              aria-label="Organization to add"
              className="min-h-10 w-full max-w-xs rounded-lg border border-slate-300 px-2 text-sm focus:border-purple-500 focus:outline-none focus:ring-2 focus:ring-purple-200"
            >
              <option value="">Choose an organization…</option>
              {addable.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!picking}
              onClick={() => {
                setOrgIds([...orgIds, picking]);
                setPicking('');
              }}
              className="min-h-10 cursor-pointer rounded-lg border border-purple-300 px-4 text-sm font-medium text-purple-700 hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
      )}

      {visibility === 'public' && orgIds.length > 0 && (
        <p className="mt-3 text-xs text-slate-500">
          {orgIds.length} organization{orgIds.length === 1 ? '' : 's'} kept for when it is made private again.
        </p>
      )}

      {(dirty || mutation.isError) && (
        <div className="mt-4 flex items-center justify-end gap-3">
          {mutation.isError && <span role="alert" className="text-sm text-red-600">Could not save. Try again.</span>}
          <button
            type="button"
            onClick={() => {
              setVisibility(skill.visibility);
              setOrgIds(skill.organizations.map((o) => o.id));
              mutation.reset();
            }}
            className="min-h-10 cursor-pointer rounded-lg px-4 text-sm font-medium text-slate-600 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending || !dirty}
            className="min-h-10 cursor-pointer rounded-lg bg-purple-600 px-4 text-sm font-semibold text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {mutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    </li>
  );
}
